/* eslint-disable */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  listAdjustments, createAdjustment, updateAdjustment, deleteAdjustment,
  parseAdjustmentsFiles,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  GitCompareArrows, Plus, Pencil, Trash2, TrendingUp, TrendingDown, FileText,
  Upload, Loader2,
} from "lucide-react";
import { toast } from "sonner";

const eur = (n) =>
  (Number(n) || 0).toLocaleString("es-ES", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2,
  });

const today = () => new Date().toISOString().slice(0, 10);

export default function Adjustments() {
  const [data, setData] = useState({ adjustments: [], pending_count: 0, pending_balance: 0, in_favor_of: "none" });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  // Form state
  const [date, setDate] = useState(today());
  const [amountAbs, setAmountAbs] = useState("");
  const [direction, setDirection] = useState("vdv"); // vdv | et
  const [description, setDescription] = useState("");

  // PDF upload state
  const fileRef = useRef(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [parsingPdf, setParsingPdf] = useState(false);

  function resetForm() {
    setDate(today());
    setAmountAbs("");
    setDirection("vdv");
    setDescription("");
    setEditing(null);
  }

  function openEdit(adj) {
    setEditing(adj);
    setDate(adj.source_date);
    const amt = adj.amount_eur;
    setAmountAbs(String(Math.abs(amt)));
    setDirection(amt >= 0 ? "vdv" : "et");
    setDescription(adj.description || "");
    setShowForm(true);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listAdjustments();
      setData(d);
    } catch {
      toast.error("Error cargando ajustes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async () => {
    const n = Number(amountAbs);
    if (!date || !n || n <= 0) {
      toast.error("Indica fecha e importe (positivo)");
      return;
    }
    const signed = direction === "vdv" ? n : -n;
    const payload = { source_date: date, amount_eur: signed, description };
    try {
      if (editing) {
        await updateAdjustment(editing.id, payload);
        toast.success("Ajuste actualizado");
      } else {
        await createAdjustment(payload);
        toast.success("Ajuste añadido");
      }
      setShowForm(false);
      resetForm();
      load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAdjustment(toDelete.id);
      toast.success("Ajuste eliminado");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  const handlePdfUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (files.length > 10) {
      toast.error("Máximo 10 archivos a la vez");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    setParsingPdf(true);
    try {
      const r = await parseAdjustmentsFiles(files);
      setPdfPreview({
        date: r.date || today(),
        dateMissing: !r.date,
        items: r.items || [],
        net_amount: r.net_amount,
        count: r.count,
        filename: files.length === 1 ? files[0].name : `${files.length} archivos`,
        source: r.source,
        emptyFiles: r.empty_files || [],
        failedFiles: r.failed_files || [],
      });
    } catch (err) {
      toast.error("Error: " + (err?.response?.data?.detail || err.message));
    } finally {
      setParsingPdf(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setPreviewItems = (items) => {
    const net = Math.round(items.reduce((s, it) => s + it.delta, 0) * 100) / 100;
    setPdfPreview((p) => ({ ...p, items, net_amount: net, count: items.length }));
  };

  const editPreviewItem = (i, field, value) => {
    const items = pdfPreview.items.map((it, j) => {
      if (j !== i) return it;
      const next = { ...it, [field]: value };
      const o = parseFloat(String(next.original).replace(",", ".")) || 0;
      const n = parseFloat(String(next.nuevo).replace(",", ".")) || 0;
      return { ...next, delta: Math.round((n - o) * 100) / 100 };
    });
    setPreviewItems(items);
  };

  const removePreviewItem = (i) => setPreviewItems(pdfPreview.items.filter((_, j) => j !== i));

  const confirmPdfAsAdjustment = async () => {
    if (!pdfPreview) return;
    try {
      // Build a description that references the names + originalPDF
      const namesPreview = pdfPreview.items.slice(0, 4).map((it) => it.name).filter(Boolean).join(", ");
      const more = pdfPreview.items.length > 4 ? ` y ${pdfPreview.items.length - 4} más` : "";
      const desc = `${pdfPreview.filename} · ${pdfPreview.count} líneas (${namesPreview}${more})`;
      await createAdjustment({
        source_date: pdfPreview.date,
        amount_eur: pdfPreview.net_amount,
        description: desc,
      });
      toast.success(`Ajuste de ${pdfPreview.net_amount.toFixed(2)} € añadido`);
      setPdfPreview(null);
      load();
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    }
  };

  if (loading) return <div className="text-slate-400 text-sm py-12 text-center">Cargando…</div>;

  const balance = data.pending_balance;
  const balanceVdv = balance > 0;
  const balanceEt = balance < 0;

  const pending = data.adjustments.filter((a) => !a.applied);
  const applied = data.adjustments.filter((a) => a.applied);

  return (
    <div data-testid="adjustments-page" className="space-y-6 max-w-5xl">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 mb-1">
            Ajustes · Discrepancias contables
          </div>
          <h1 className="font-cabinet text-3xl font-bold tracking-tight text-slate-900">
            Ajustes pendientes de aplicar
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            Cada vez que Susana envía un correo con ajustes (Cliente, sin IVA),
            calcula la suma neta y añádela aquí. Al emitir la próxima factura
            <strong> Final</strong>, podrás aplicar el balance acumulado como una
            línea &ldquo;Pricing Discrepancies&rdquo; y el contador volverá a cero.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="file"
            ref={fileRef}
            accept=".pdf,.png,.jpg,.jpeg,.webp"
            multiple
            onChange={handlePdfUpload}
            className="hidden"
            data-testid="adj-pdf-input"
          />
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={parsingPdf}
            data-testid="upload-pdf-btn"
          >
            {parsingPdf
              ? <Loader2 size={15} className="mr-2 animate-spin" />
              : <Upload size={15} className="mr-2" />}
            {parsingPdf ? "Leyendo…" : "Subir PDF o capturas"}
          </Button>
          <Button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-adjustment-btn"
          >
            <Plus size={15} className="mr-2" /> Añadir manualmente
          </Button>
        </div>
      </header>

      {/* Balance KPI */}
      <div className={`rounded-lg border-2 p-6 ${
        balanceVdv ? "bg-amber-50 border-amber-300" :
        balanceEt ? "bg-sky-50 border-sky-300" :
        "bg-slate-50 border-slate-200"
      }`}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 mb-1">
              Balance acumulado pendiente de cargar
            </div>
            <div className={`font-cabinet text-4xl font-bold ${
              balanceVdv ? "text-amber-700" : balanceEt ? "text-sky-700" : "text-slate-500"
            }`} data-testid="adjustment-balance">
              {balance === 0 ? "0,00 €" : eur(Math.abs(balance))}
            </div>
            <div className="text-sm text-slate-600 mt-1">
              {balanceVdv && <>A favor de <strong>VdV</strong> (ET pagará extra en la próxima factura)</>}
              {balanceEt && <>A favor de <strong>ET</strong> (VdV restará en la próxima factura)</>}
              {balance === 0 && <>Sin ajustes pendientes</>}
            </div>
          </div>
          <GitCompareArrows
            size={48}
            className={balanceVdv ? "text-amber-300" : balanceEt ? "text-sky-300" : "text-slate-300"}
            strokeWidth={1.2}
          />
        </div>
        {pending.length > 0 && (
          <div className="mt-4 pt-4 border-t border-current/20 text-xs text-slate-600">
            <strong>{pending.length}</strong> {pending.length === 1 ? "ajuste pendiente" : "ajustes pendientes"} ·
            Se aplicarán automáticamente desde el editor de la próxima factura Final
          </div>
        )}
      </div>

      {/* Pending list */}
      <section>
        <h2 className="font-cabinet text-lg font-semibold mb-3">
          Pendientes <span className="text-slate-400 font-normal text-sm">({pending.length})</span>
        </h2>
        {pending.length === 0 ? (
          <div className="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg py-8 text-center">
            No hay ajustes pendientes. Añade uno cuando recibas un correo de Susana.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Fecha</th>
                  <th className="text-left px-4 py-2.5 font-medium">A favor de</th>
                  <th className="text-right px-4 py-2.5 font-medium">Importe</th>
                  <th className="text-left px-4 py-2.5 font-medium">Notas</th>
                  <th className="px-2 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {pending.map((a) => {
                  const isVdv = a.amount_eur >= 0;
                  return (
                    <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`adjustment-row-${a.id}`}>
                      <td className="px-4 py-2.5 text-xs text-slate-600">{a.source_date}</td>
                      <td className="px-4 py-2.5">
                        <Badge className={`${isVdv ? "bg-amber-100 text-amber-900" : "bg-sky-100 text-sky-900"} border-0 text-[10px] font-medium`}>
                          {isVdv ? <><TrendingUp size={10} className="inline mr-1" />VdV</> : <><TrendingDown size={10} className="inline mr-1" />ET</>}
                        </Badge>
                      </td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${isVdv ? "text-amber-700" : "text-sky-700"}`}>
                        {isVdv ? "+" : "−"}{eur(Math.abs(a.amount_eur))}
                      </td>
                      <td className="px-4 py-2.5 text-xs text-slate-600 max-w-md truncate" title={a.description}>
                        {a.description || <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-2 py-2 flex gap-1">
                        <button
                          onClick={() => openEdit(a)}
                          className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded"
                          data-testid={`edit-adj-${a.id}`}
                          title="Editar"
                        ><Pencil size={13} /></button>
                        <button
                          onClick={() => setToDelete(a)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded"
                          data-testid={`delete-adj-${a.id}`}
                          title="Eliminar"
                        ><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Applied history */}
      {applied.length > 0 && (
        <section>
          <h2 className="font-cabinet text-lg font-semibold mb-3 text-slate-700">
            Histórico aplicados <span className="text-slate-400 font-normal text-sm">({applied.length})</span>
          </h2>
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Fecha</th>
                  <th className="text-right px-4 py-2.5 font-medium">Importe</th>
                  <th className="text-left px-4 py-2.5 font-medium">Aplicado en</th>
                  <th className="text-left px-4 py-2.5 font-medium">Notas</th>
                </tr>
              </thead>
              <tbody>
                {applied.slice(0, 20).map((a) => (
                  <tr key={a.id} className="border-t border-slate-100 text-slate-500">
                    <td className="px-4 py-2.5 text-xs">{a.source_date}</td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                      {a.amount_eur >= 0 ? "+" : "−"}{eur(Math.abs(a.amount_eur))}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      {a.applied_invoice_id ? (
                        <Link to={`/factura/${a.applied_invoice_id}`} className="text-slate-700 hover:text-slate-900 underline-offset-2 hover:underline">
                          <FileText size={11} className="inline mr-1" />
                          {a.applied_invoice_number}
                        </Link>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs max-w-md truncate" title={a.description}>
                      {a.description || <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Form Dialog */}
      <AlertDialog open={showForm} onOpenChange={(o) => { if (!o) { setShowForm(false); resetForm(); } }}>
        <AlertDialogContent data-testid="adjustment-form-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{editing ? "Editar ajuste" : "Añadir ajuste"}</AlertDialogTitle>
            <AlertDialogDescription>
              Suma neta del correo (importe sin IVA). El signo se aplica según
              &ldquo;A favor de…&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Fecha del correo</label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="adj-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Importe (€, sin signo)</label>
              <Input
                type="number" step="0.01" min="0"
                value={amountAbs}
                onChange={(e) => setAmountAbs(e.target.value)}
                placeholder="Ej: 487,45"
                data-testid="adj-amount"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">A favor de</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDirection("vdv")}
                  className={`flex-1 px-3 py-2 rounded border-2 text-xs font-semibold transition-colors ${
                    direction === "vdv" ? "bg-amber-100 border-amber-400 text-amber-900" : "bg-white border-slate-200 text-slate-500"
                  }`}
                  data-testid="adj-dir-vdv"
                >
                  <TrendingUp size={13} className="inline mr-1" /> VdV (ET paga +)
                </button>
                <button
                  type="button"
                  onClick={() => setDirection("et")}
                  className={`flex-1 px-3 py-2 rounded border-2 text-xs font-semibold transition-colors ${
                    direction === "et" ? "bg-sky-100 border-sky-400 text-sky-900" : "bg-white border-slate-200 text-slate-500"
                  }`}
                  data-testid="adj-dir-et"
                >
                  <TrendingDown size={13} className="inline mr-1" /> ET (VdV resta)
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Notas (opcional)</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ej: Correo Susana 16-may-26 — 8 clientes (Margaret Rose, Vicky Yau, etc.)"
                rows={2}
                data-testid="adj-description"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="adj-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} className="bg-slate-900 hover:bg-slate-800" data-testid="adj-save">
              {editing ? "Guardar" : "Añadir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Preview Dialog */}
      <AlertDialog open={!!pdfPreview} onOpenChange={(o) => !o && setPdfPreview(null)}>
        <AlertDialogContent data-testid="pdf-preview-dialog" className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Vista previa de los ajustes</AlertDialogTitle>
            <AlertDialogDescription>
              Se detectaron <strong>{pdfPreview?.count}</strong> líneas en{" "}
              <span className="font-mono">{pdfPreview?.filename}</span>.
              Revisa y corrige los importes si hace falta; se añadirá un único ajuste con el neto total.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pdfPreview && (
            <div className="space-y-3 min-w-0">
              {pdfPreview.source === "ai" && (
                <div data-testid="pdf-preview-ai-notice" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Hay capturas o imágenes: los datos se han leído con IA. Comprueba nombres, importes y fecha antes de guardar.
                </div>
              )}
              {pdfPreview.emptyFiles?.length > 0 && (
                <div data-testid="pdf-preview-empty-files" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                  Sin líneas detectadas en: {pdfPreview.emptyFiles.join(", ")}
                </div>
              )}
              {pdfPreview.failedFiles?.length > 0 && (
                <div data-testid="pdf-preview-failed-files" className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                  No se pudieron leer: {pdfPreview.failedFiles.map((f) => `${f.name} (${f.error})`).join(", ")}
                </div>
              )}
              <div className="flex items-center justify-between rounded-md bg-slate-50 px-4 py-3 border border-slate-200">
                <div className="text-xs">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Fecha del correo</div>
                  <Input
                    type="date"
                    value={pdfPreview.date}
                    onChange={(e) => setPdfPreview((p) => ({ ...p, date: e.target.value }))}
                    className="h-8 w-40 text-xs"
                    data-testid="pdf-preview-date"
                  />
                  {pdfPreview.dateMissing && (
                    <div data-testid="pdf-preview-date-missing" className="mt-1 text-[10px] text-amber-700">
                      No se encontró la fecha: se ha puesto la de hoy
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Neto a añadir</div>
                  <div data-testid="pdf-preview-net" className={`font-cabinet font-bold text-2xl ${
                    pdfPreview.net_amount >= 0 ? "text-amber-700" : "text-sky-700"
                  }`}>
                    {pdfPreview.net_amount >= 0 ? "+" : "−"}{eur(Math.abs(pdfPreview.net_amount))}
                  </div>
                  <div className="text-[10px] text-slate-500">
                    {pdfPreview.net_amount === 0 ? "sin saldo" : `a favor de ${pdfPreview.net_amount > 0 ? "VdV" : "ET"}`}
                  </div>
                </div>
              </div>

              <div className="max-h-64 overflow-auto border border-slate-200 rounded-md">
                <table className="w-full table-fixed text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Cliente / Concepto</th>
                      <th className="w-[4.5rem] sm:w-24 text-right px-1 sm:px-3 py-2 font-medium">Original</th>
                      <th className="w-[4.5rem] sm:w-24 text-right px-1 sm:px-3 py-2 font-medium">Nuevo</th>
                      <th className="w-16 sm:w-20 text-right px-1 sm:px-3 py-2 font-medium">Δ</th>
                      <th className="w-7 px-1 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pdfPreview.items.map((it, i) => (
                      <tr key={i} className="border-t border-slate-100" data-testid={`pdf-preview-row-${i}`}>
                        <td className="px-2 sm:px-3 py-1.5 break-words sm:truncate" title={it.name}>{it.name || "—"}</td>
                        <td className="px-2 py-1">
                          <Input
                            inputMode="decimal"
                            value={it.original}
                            onChange={(e) => editPreviewItem(i, "original", e.target.value)}
                            className="h-7 w-full px-1 sm:px-3 text-right font-mono text-xs"
                            data-testid={`pdf-preview-original-${i}`}
                          />
                        </td>
                        <td className="px-2 py-1">
                          <Input
                            inputMode="decimal"
                            value={it.nuevo}
                            onChange={(e) => editPreviewItem(i, "nuevo", e.target.value)}
                            className="h-7 w-full px-1 sm:px-3 text-right font-mono text-xs"
                            data-testid={`pdf-preview-nuevo-${i}`}
                          />
                        </td>
                        <td data-testid={`pdf-preview-delta-${i}`} className={`px-3 py-1.5 text-right font-mono font-semibold ${
                          it.delta >= 0 ? "text-amber-700" : "text-sky-700"
                        }`}>
                          {it.delta >= 0 ? "+" : ""}{it.delta.toFixed(2)}
                        </td>
                        <td className="px-2 py-1 text-right">
                          <button
                            type="button"
                            onClick={() => removePreviewItem(i)}
                            className="text-slate-400 hover:text-rose-600 transition-colors"
                            title="Quitar línea"
                            data-testid={`pdf-preview-remove-${i}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pdf-preview-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmPdfAsAdjustment(); }}
              disabled={!pdfPreview?.items?.length}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="pdf-preview-confirm"
            >
              Añadir ajuste
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar ajuste?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará el ajuste de {toDelete?.source_date} por{" "}
              {toDelete && eur(Math.abs(toDelete.amount_eur))}. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
